import React, {useContext, useEffect, useState} from 'react';
import AuthContext from '../../contexts/AuthContext';
import Page from '../Page';
import useAPI from "../../hooks/useAPI";
import ContractsStepper from "../ContractsStepper";
import {getStepNamesForContractStepper} from "../../services/incompleteAgreementsService";
import PaymentMethodSelector from '../PaymentMethodSelector'
import './SystemContract.scss'

export default function SystemContractSigning(
    {
        templateTypeKey,
        systemId,
        agreeURL,
        clickWrapId = null,
        documentData = {}
    }
) {
    const {user, incompleteSystemsAgreements, onLogout, system} = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);
    const [url, setUrl] = useState(null);
    const [envelopeId, setEnvelopeId] = useState(null);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const [sessionTimeout, setSessionTimeout] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [error, setError] = useState(null);
    const [hasUpfrontPayment, setHasUpfrontPayment] = useState(false);
    const [completedCount, setCompletedCount] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState(null)

    const api = useAPI();

    const redirectToSystemRegistration = () => {
        if (paymentMethod === 'ACH') {
            api.updateShowPlaidLinkBankAccountPopUp(true)
            window.location.replace('/account/payment-settings');
            return;
        }
        window.location.replace('/');
    };

    const setPaymentMethodStateParam = (paymentMethod) => {
        api.cookie('user', {...user, payment_method_type: paymentMethod});
        setPaymentMethod(paymentMethod)
        setCompletedCount(prevCompletedCount => prevCompletedCount + 1);
        setIsCompleted(true)
    }

    const updateSystemAgreement = (agreementKey, params = {}) => {
        setIsDisabled(true)
        api.json('POST', `/systems/${systemId}/customer-contract/${agreeURL}`, params)
            .then(r => r.json())
            .then((response) => {
                const updatedSystemsData = incompleteSystemsAgreements.map(item => {
                    if (item.id === systemId) {
                        if (item.incomplete_agreements && item.incomplete_agreements[agreementKey]) {
                            const agreement = item.incomplete_agreements[agreementKey];
                            agreement.is_completed = true;
                            if (agreementKey === 'srec_agreement') {
                                agreement.has_upfront_payment = response.has_upfront_payment;
                                setHasUpfrontPayment(response.has_upfront_payment);
                            }
                            setCompletedCount(prevCompletedCount => prevCompletedCount + 1);
                        }
                    }
                    return item;
                });

                api.updateIncompleteSystemsAgreementsData(updatedSystemsData);
                setIsDisabled(false)
            })
            // .then(() => window.location.reload(true))
            .catch(e => alert(e));
    }

    useEffect(() => {
        if (templateTypeKey === 'payment_method_selector') {
            setLoading(false);
            return;
        }

        if (clickWrapId !== null) {
            const userId = process.env.NODE_ENV === 'production' ? user.id : Math.floor(Math.random() * 100000) + 1;
            window.docuSignClick.Clickwrap.render({
                environment: process.env.REACT_APP_DOCUSIGN_CLICK_ENVIRONMENT,
                accountId: process.env.REACT_APP_DOCUSIGN_ACCOUNT_ID,
                clickwrapId: clickWrapId,
                clientUserId: userId,
                documentData: documentData,
                onAgreed: function () {
                    console.log('agreed', hasErrorOccurred)
                    updateSystemAgreement(templateTypeKey);
                    setIsCompleted(true);
                },
                onError: function (error) {
                    setHasErrorOccurred(true);
                }
            }, '#ds-clickwrap');
        } else {
            api.json('GET', `/systems/${systemId}/customer-contract/url`, {
                userId: user.id
            })
                .then(r => r.json())
                .then((response) => {
                    if (response.error) {
                        setHasErrorOccurred(true);
                        setError(response.error);
                    } else {
                        setUrl(response.url || null);
                        setEnvelopeId(response.envelopeId || null);
                    }
                })
                .catch(e => {
                    alert(e);
                    setHasErrorOccurred(true);
                }).finally(() => setLoading(false))
        }
        // eslint-disable-next-line
    }, []);

    //added to show incomplete contracts based on system which user selected in CFP
    useEffect(() => {
        const selectedAgreement = incompleteSystemsAgreements.find(agreement => agreement.id === system.id);

        if (selectedAgreement) {
            const agreements = Object.values(selectedAgreement.incomplete_agreements || {});

            const agreementWithUpfrontPayment = agreements.find(agreement => agreement.hasOwnProperty('has_upfront_payment'));
            setHasUpfrontPayment(agreementWithUpfrontPayment?.has_upfront_payment || false);

            setCompletedCount(agreements.filter(agreement => agreement.is_completed === true).length);
        }
        // eslint-disable-next-line
    }, [incompleteSystemsAgreements]);

    useEffect(() => {
        if (url !== null && templateTypeKey !== 'payment_method_selector') {
            window.DocuSign.loadDocuSign(process.env.REACT_APP_DOCUSIGN_CLIENT_ID)
                .then((docusign) => {
                    const signing = docusign.signing({
                        url: url,
                        displayFormat: 'focused',
                    });

                    signing.on('ready', (event) => {
                        console.log('UI is rendered');
                    });

                    signing.on('sessionEnd', (event) => {
                        console.log('Session ended with cause:', event.sessionEndType);
                        console.log(event)
                        if (event.sessionEndType === 'signing_complete') {
                            updateSystemAgreement(templateTypeKey, {envelope_id: envelopeId});
                            setIsCompleted(true);
                        }

                        if (event.sessionEndType === 'session_timeout') {
                            setSessionTimeout(true);
                        }
                    });

                    signing.mount('#agreement');
                })
                .catch((ex) => {
                    console.error('Error loading DocuSign:', ex);
                });
        }
        // eslint-disable-next-line
    }, [url]);

    const stepNames = getStepNamesForContractStepper(incompleteSystemsAgreements, systemId, user.payment_method_type);

    return (
        <Page title="Contract Signing" padded="true" minWidth={800}>
            <div>
                {(templateTypeKey !== 'payment_method_selector' && completedCount !== stepNames.length) && (
                    <div className="mb-10" style={{display: "flex", justifyContent: "space-between"}}>
                        <button onClick={onLogout} className="ms-auto">Logout</button>
                    </div>
                )}
                <ContractsStepper stepNames={stepNames} currentStep={completedCount}/>
                {sessionTimeout && (
                    <div style={{textAlign: 'center', marginTop: "200px"}}>
                        <p style={{color: "red", fontSize: "18px"}}>
                            Your signing agreement session has expired. Please refresh the page.
                        </p>
                    </div>
                )}

                {!paymentMethod && templateTypeKey === 'payment_method_selector' && (
                    <PaymentMethodSelector setPaymentMethodStateParam={setPaymentMethodStateParam}/>
                )}
                {isCompleted && (
                    <div className="d-flex flex-column align-items-center justify-content-center vh-100"
                         style={{marginTop: "200px"}}
                    >
                        <div>
                            <div style={{fontSize: "40px", color: "#6ECEB2", fontWeight: "bold"}}>✔</div>
                        </div>
                        <p className="mt-3 text-success fw-bold" style={{fontSize: "18px", textAlign: "center"}}>
                            {
                                completedCount === stepNames.length
                                    ? (
                                        <span style={{maxWidth: "60%", lineHeight: 1.5, display: "inline-block"}}>
                                            Thank you for signing the REC Agreement. Please note that this agreement still
                                            requires countersignature by our team. You can expect a fully executed copy within 2-3 business days,
                                            and once countersigned, a final copy will be emailed to you.
                                        </span>
                                    )
                                    : "Successfully signed, continue to step: " + stepNames[completedCount]
                            }
                        </p>
                        {templateTypeKey === 'srec_agreement' && hasUpfrontPayment && (
                            <p className="mt-3 text-success fw-bold" style={{fontSize: "18px"}}>
                                Since you marked the upfront payment on the previous agreement, you have to sign another
                                agreement
                            </p>)}

                        <button disabled={isDisabled} className="contract-continue mt-3" onClick={redirectToSystemRegistration}>
                            {completedCount === stepNames.length ? "Go to dashboard" : "Continue"}
                        </button>
                    </div>
                )}
                {hasErrorOccurred && (
                    <div style={{textAlign: 'center', marginTop: "200px"}}>
                        <p style={{color: "red", fontSize: "18px"}}>
                            We are experiencing technical difficulties to load agreement, please try again later!
                        </p>
                        {error && (
                            <div>
                                <br/>
                                <p style={{color: "red", fontSize: "18px"}}>{`Error: ${error}`}</p>
                            </div>
                        )}
                    </div>
                )}
                {templateTypeKey !== 'payment_method_selector' && (
                    clickWrapId ? (
                        <div>
                            <div id="ds-clickwrap"></div>
                        </div>
                    ) : (
                        loading ? (
                            <div>
                                <div className="component-busy-container">
                                    <div className="component-busy">
                                        <div className="component-busy--spinner">
                                            <span></span>
                                        </div>
                                        <span style={{marginLeft: 10, fontSize: "18px"}}>
                                            Please wait while we load agreement for you, it may take up to 1 minute!
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                style={{height: window.innerHeight - 200}}
                                className="docusign-agreement"
                                id="agreement"
                            />
                        )
                    )
                )}
            </div>
        </Page>
    );
};